import { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { FormikHelpers } from 'formik';

import { CodingStrategyEnum, getDocumentName } from '@e-socna/data-layer';
import { buildPath } from '@e-socna/utils';
import { EMPTY_STRING } from '@e-socna/empty';
import type { CodingConfigManual, Id } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../../utils/back-errors.util';
import { PATH_MISSION_REPORT_DEFAULT } from '../../../app/constants/paths.constant';
import { useMissionReadyContext } from '../../../contexts/MissionContext/hooks/useMissionReadyContext.hook';
import type { BackError } from '../../../utils/back-errors.util';

import { MissionPage_OneMission_QueryDocument } from '../../Mission/hooks/useFindOneMissionQuery.hook';

import { useInitReportInfoMutation } from '../hooks/useInitInfoMutation.hook';
import type { ReportInfo_Init_Result } from '../hooks/useInitInfoMutation.hook';
import type { MissionReportWithItsInfo } from '../types/mission-report-with-its-info.type';
import { MISSION_SPACE_PARAM } from '../../../app/utils/mission-space.util';
import { useMissionSpace } from '../../../hooks/useMissionSpace.hook';
import { MessageList } from '../../../components/MessageList';
import { documentChoiceForm2ApiMapper } from '../../../forms/DocumentTemplates/mappers/document-choice.form2api.mapper';

export const useInitHandler = () => {
  const { folderId, missionId } = useMissionReadyContext();
  const missionSpace = useMissionSpace();

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { mutate: initReportInfoMutation } = useInitReportInfoMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const goToReportEditor = useCallback(
    (reportInfoId: Id) => {
      void queryClient.invalidateQueries({
        queryKey: [
          getDocumentName(MissionPage_OneMission_QueryDocument),
          { id: missionId },
        ],
      });

      enqueueSnackbar('Le rapport a bien été initialisé.');

      void navigate(
        buildPath(PATH_MISSION_REPORT_DEFAULT, {
          folderId,
          missionId,
          reportInfoId,
          [MISSION_SPACE_PARAM]: missionSpace,
        }),
      );
    },
    [enqueueSnackbar, folderId, missionId, missionSpace, navigate, queryClient],
  );

  const onInit = useCallback(
    (handleSuccess?: () => void) =>
      (
        {
          codingConfig,
          info,
          templateId,
          title,
        }: Readonly<MissionReportWithItsInfo>,
        { setSubmitting }: Readonly<FormikHelpers<MissionReportWithItsInfo>>,
      ) => {
        setIsSubmitting(true);

        initReportInfoMutation(
          {
            missionId,
            reportInfo: { workflowId: info.workflowId },
            ...documentChoiceForm2ApiMapper({ templateId }),
            title: title ?? EMPTY_STRING,
            code:
              codingConfig.kind === CodingStrategyEnum.CodingConfigManual
                ? (codingConfig as CodingConfigManual).code
                : EMPTY_STRING,
          },
          {
            onSuccess: ({
              initReportInfo,
            }: Readonly<ReportInfo_Init_Result>) => {
              const { reportInfos } = initReportInfo;
              const newReportInfo = reportInfos[reportInfos.length - 1];

              if (newReportInfo) {
                goToReportEditor(newReportInfo._id);
              }

              handleSuccess?.();
            },
            onError: (error: unknown) => {
              enqueueSnackbar(
                <MessageList
                  messages={userFriendlyErrors(error as BackError)}
                />,
                {
                  variant: 'error',
                },
              );
            },
            onSettled: () => {
              setIsSubmitting(false);
              setSubmitting(false);
            },
          },
        );
      },
    [enqueueSnackbar, goToReportEditor, initReportInfoMutation, missionId],
  );

  return { isSubmitting, onInit };
};
